/**
 * Road geometry enrichment.
 *
 * Thin client wrapper around the `enrichClosureGeometry` Cloud Function,
 * which calls Valhalla's `/locate` (point closures) or `/trace_attributes`
 * (route closures) to extract intersection nodes, road bearings, and
 * cross-street bearings.
 *
 * The results are stored alongside each closure at save time so that
 * `src/services/closurePolygons.ts` can build road-aware "stop bar"
 * exclude polygons for Valhalla instead of blanket 30m squares.
 *
 * All calls are best-effort — if enrichment fails (network error,
 * Valhalla rejects the request, etc.) we resolve with `null` and the
 * caller stores the closure without geometry. The polygon generator
 * falls back to today's shapes for closures missing this data, so
 * pre-enrichment closures continue to work.
 *
 * Companion:
 *   - Cloud Function source: functions/index.js (enrichClosureGeometry)
 *   - Design doc: .cursor/rules/closure-polygons-phase4-5-plan.mdc
 */

import type {PointRoadGeometry, RouteRoadGeometry} from './api';

const ENRICH_FUNCTION_URL =
  'https://us-central1-respondermap.cloudfunctions.net/enrichClosureGeometry';

// The Cloud Function has its own 20s Valhalla timeout. This is our
// client-side wall-clock cap so a single stalled request doesn't hang
// the "save closure" flow.
const ENRICH_REQUEST_TIMEOUT_MS = 15000;

interface EnrichPointResponse {
  success: boolean;
  type?: 'point';
  snappedLatitude?: number;
  snappedLongitude?: number;
  roadBearing?: number;
  atIntersection?: boolean;
  reason?: string;
}

interface EnrichRouteResponse {
  success: boolean;
  type?: 'route';
  intersections?: Array<{
    latitude: number;
    longitude: number;
    closedRoadBearing: number;
    crossStreetBearings: number[];
  }>;
  startEndpoint?: {
    latitude: number;
    longitude: number;
    closedRoadBearing: number;
  } | null;
  endEndpoint?: {
    latitude: number;
    longitude: number;
    closedRoadBearing: number;
  } | null;
  reason?: string;
}

async function callEnrichmentEndpoint(
  body: Record<string, unknown>,
): Promise<EnrichPointResponse | EnrichRouteResponse | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ENRICH_REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(ENRICH_FUNCTION_URL, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (networkErr: any) {
    clearTimeout(timeoutId);
    if (networkErr?.name === 'AbortError') {
      console.warn('🛣️ Road geometry enrichment timed out');
    } else {
      console.warn('🛣️ Road geometry enrichment network error:', networkErr?.message || networkErr);
    }
    return null;
  }
  clearTimeout(timeoutId);

  if (!response.ok) {
    console.warn(`🛣️ Road geometry enrichment HTTP ${response.status}`);
    return null;
  }

  try {
    return (await response.json()) as EnrichPointResponse | EnrichRouteResponse;
  } catch (parseErr: any) {
    console.warn('🛣️ Road geometry enrichment: unparseable response', parseErr?.message);
    return null;
  }
}

/**
 * Enrich a point closure by asking Valhalla for the nearest road's
 * bearing and whether the closure sits at an intersection.
 *
 * Returns null on any failure. Caller should save the closure without
 * pointGeometry — the polygon generator falls back to a 30m square.
 */
export async function fetchPointRoadGeometry(
  latitude: number,
  longitude: number,
): Promise<PointRoadGeometry | null> {
  const data = (await callEnrichmentEndpoint({
    type: 'point',
    point: {latitude, longitude},
  })) as EnrichPointResponse | null;

  if (!data || !data.success) {
    if (data?.reason) console.warn('🛣️ Point enrichment declined:', data.reason);
    return null;
  }
  if (
    typeof data.snappedLatitude !== 'number' ||
    typeof data.snappedLongitude !== 'number' ||
    typeof data.roadBearing !== 'number' ||
    typeof data.atIntersection !== 'boolean'
  ) {
    console.warn('🛣️ Point enrichment: response missing required fields');
    return null;
  }

  return {
    snappedLatitude: data.snappedLatitude,
    snappedLongitude: data.snappedLongitude,
    roadBearing: data.roadBearing,
    atIntersection: data.atIntersection,
  };
}

/**
 * Enrich a route closure by asking Valhalla's `/trace_attributes` to
 * identify every intersection along the closed road's polyline plus
 * the polyline's start/end nodes.
 *
 * Returns null on any failure. Caller should save the closure without
 * routeGeometry — the polygon generator falls back to today's sampled
 * squares.
 */
export async function fetchRouteRoadGeometry(
  polyline: Array<{latitude: number; longitude: number}>,
): Promise<RouteRoadGeometry | null> {
  if (!Array.isArray(polyline) || polyline.length < 2) return null;

  const data = (await callEnrichmentEndpoint({
    type: 'route',
    polyline,
  })) as EnrichRouteResponse | null;

  if (!data || !data.success) {
    if (data?.reason) console.warn('🛣️ Route enrichment declined:', data.reason);
    return null;
  }

  const intersections = Array.isArray(data.intersections)
    ? data.intersections.filter(
        ix =>
          typeof ix.latitude === 'number' &&
          typeof ix.longitude === 'number' &&
          typeof ix.closedRoadBearing === 'number' &&
          Array.isArray(ix.crossStreetBearings) &&
          ix.crossStreetBearings.every(b => typeof b === 'number'),
      )
    : [];

  const startEndpoint =
    data.startEndpoint &&
    typeof data.startEndpoint.latitude === 'number' &&
    typeof data.startEndpoint.longitude === 'number' &&
    typeof data.startEndpoint.closedRoadBearing === 'number'
      ? data.startEndpoint
      : null;

  const endEndpoint =
    data.endEndpoint &&
    typeof data.endEndpoint.latitude === 'number' &&
    typeof data.endEndpoint.longitude === 'number' &&
    typeof data.endEndpoint.closedRoadBearing === 'number'
      ? data.endEndpoint
      : null;

  return {intersections, startEndpoint, endEndpoint};
}
